"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppRouteAccessSchema = exports.AppPageAccessSchema = exports.AppAccessGroupGrantSchema = exports.appAccessAudienceRecord = exports.AppTableAudienceAccessSchema = exports.AppAudienceAccessSchema = exports.AppInactiveAudienceSchema = exports.AppAccessRightsSchema = exports.AppAccessChangeSchema = exports.AppRowScopeSchema = exports.AppAccessTemplateSchema = exports.AppAccessAudienceSchema = void 0;
const zod_1 = require("zod");
/**
 * The words and shapes of page rights both the App manifest and the access
 * section use; a leaf module, so a page of an App can carry its rights without
 * importing how they are resolved (app-page-access).
 */
exports.AppAccessAudienceSchema = zod_1.z.enum(['internal', 'portal', 'public']);
/** The four starting points the Studio offers per audience. */
exports.AppAccessTemplateSchema = zod_1.z.enum([
    'viewOnly',
    'submit',
    'manageOwn',
    'editAll',
]);
/** Rows a right reaches: own rows, rows of the person's groups, all rows. */
exports.AppRowScopeSchema = zod_1.z.enum(['own', 'group', 'all']);
/** An extra right: off, only own rows, or the rows reading reaches. */
exports.AppAccessChangeSchema = zod_1.z.enum(['off', 'own', 'likeRead']);
exports.AppAccessRightsSchema = zod_1.z
    .object({
    read: exports.AppRowScopeSchema,
    create: zod_1.z.boolean().default(false),
    update: exports.AppAccessChangeSchema.default('off'),
    delete: exports.AppAccessChangeSchema.default('off'),
})
    .strict();
/** An audience the page or table is not meant for. */
exports.AppInactiveAudienceSchema = zod_1.z
    .object({ active: zod_1.z.literal(false) })
    .strict();
exports.AppAudienceAccessSchema = zod_1.z.discriminatedUnion('active', [
    exports.AppInactiveAudienceSchema,
    exports.AppAccessRightsSchema.extend({ active: zod_1.z.literal(true) }).strict(),
]);
/** Only a table names single columns that stay read-only. */
exports.AppTableAudienceAccessSchema = zod_1.z.discriminatedUnion('active', [
    exports.AppInactiveAudienceSchema,
    exports.AppAccessRightsSchema.extend({
        active: zod_1.z.literal(true),
        readOnlyColumns: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    }).strict(),
]);
/** One value per audience; an audience left out has no own setting. */
const appAccessAudienceRecord = (value) => zod_1.z
    .object({
    internal: value.optional(),
    portal: value.optional(),
    public: value.optional(),
})
    .strict();
exports.appAccessAudienceRecord = appAccessAudienceRecord;
/** Extra rights of the persons of one group, added to their audience. */
exports.AppAccessGroupGrantSchema = zod_1.z
    .object({
    groupId: zod_1.z.string().min(1),
    audience: exports.AppAccessAudienceSchema,
    sourceKey: zod_1.z.string().min(1).optional(),
    create: zod_1.z.boolean().default(false),
    update: exports.AppAccessChangeSchema.default('off'),
    delete: exports.AppAccessChangeSchema.default('off'),
})
    .strict();
exports.AppPageAccessSchema = zod_1.z
    .object({
    audiences: (0, exports.appAccessAudienceRecord)(exports.AppAudienceAccessSchema).prefault({}),
    tables: zod_1.z
        .record(zod_1.z.string().min(1), (0, exports.appAccessAudienceRecord)(exports.AppTableAudienceAccessSchema))
        .prefault({}),
    groups: zod_1.z.array(exports.AppAccessGroupGrantSchema).default([]),
})
    .strict();
/**
 * The page rights an App brings for one of its pages, in the format of the
 * access section of a container: per audience the page, per table of the page
 * its own rights and read-only columns. Groups belong to the container that
 * knows them. A table names at least one audience.
 */
exports.AppRouteAccessSchema = exports.AppPageAccessSchema.omit({ groups: true })
    .strict()
    .superRefine((access, ctx) => {
    for (const [sourceKey, table] of Object.entries(access.tables)) {
        if (Object.keys(table).length > 0)
            continue;
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `Page rights of table ${sourceKey} name no audience`,
            path: ['tables', sourceKey],
        });
    }
});
